import { useCallback, useEffect, useRef, useState } from "react";
import what1 from "assets/png/what1.png";
import what2 from "assets/png/what2.png";
import what3 from "assets/png/what3.png";
import what4 from "assets/png/what4.png";
import clock from "assets/svg/clock.svg";
import { useModal, ModalType } from "context/modal-context";

// ─── Data ────────────────────────────────────────────────────────────────────

interface Service {
  label: string;
  image: string;
  title: string;
  description: string[];
  tags: string[];
  badge?: string;
  cta: string;
  modal: ModalType;
}

const SERVICES: Service[] = [
  {
    label: "Tech Talent Recruitment and Outsourcing",
    image: what1,
    title: "Access Pre-Vetted Technology Talent",
    description: [
      "We help organizations hire and deploy highly skilled technology professionals across critical roles.",
      "Our recruitment and outsourcing model ensures companies gain access to qualified talent quickly while maintaining delivery quality and operational stability.",
    ],
    tags: [
      "Software Engineering",
      "DevOps Engineering",
      "Data and AI",
      "Business Analysis",
      "Design",
      "Product Management",
      "Cybersecurity",
      "More...",
    ],
    badge: "24 – 48 hours",
    cta: "Request for Talents",
    modal: "hire-talent",
  },
  {
    label: "Web and Software Development / Project Delivery",
    image: what2,
    title: "Technology Project Delivery",
    description: [
      "Conclase partners with organizations to design, build, and deliver digital products and enterprise systems.",
      "We support both end-to-end development and project-based engineering support, helping businesses accelerate innovation while maintaining quality and reliability.",
    ],
    tags: [
      "Web Application Development",
      "Enterprise Systems",
      "Fintech Platforms",
      "API Integrations",
      "Cloud Infrastructure",
      "Product Engineering",
    ],
    cta: "Start a Project",
    modal: "start-project",
  },
  {
    label: "Managed Technology Services",
    image: what3,
    title: "Managed Services and Technical Support",
    description: [
      "For organisations that require ongoing technical expertise, Conclase provides managed services and dedicated technology teams.",
      "This allows organisations to maintain high-performing systems without the complexity of managing large internal teams.",
    ],
    tags: [
      "Application Support (L2 / L3)",
      "DevOps and Infrastructure Management",
      "Enterprise Systems Support",
      "Technical Operations",
      "Dedicated Engineering Teams",
    ],
    cta: "Discuss Managed Services",
    modal: "managed-service",
  },
  {
    label: "Corporate and Bespoke Training",
    image: what4,
    title: "Corporate Technology Training and Workforce Development",
    description: [
      "For organisations that require ongoing technical expertise, Conclase provides managed services and dedicated technology teams.",
      "This allows organisations to maintain high-performing systems without the complexity of managing large internal teams.",
    ],
    tags: [
      "On-site and virtual training programs",
      "Custom curriculum design",
      "Team upskilling and reskilling",
      "Graduate training programs",
      "Workforce development partnerships",
    ],
    cta: "Request Corporate Training",
    modal: "corporate-training",
  },
];

const AUTO_SWITCH_MS = 4000;

// ─── Sub-components ───────────────────────────────────────────────────────────

interface TabItemProps {
  label: string;
  active: boolean;
  onClick: () => void;
}

const TabItem = ({ label, active, onClick }: TabItemProps) => (
  <button
    onClick={onClick}
    className={`text-left py-3 pl-4 border-l-2 transition-colors duration-300 text-sm leading-snug w-full ${
      active
        ? "border-yellow-100 text-white font-semibold bg-white/[0.07]"
        : "border-white/10 text-white/40 font-normal hover:text-white/70 hover:border-white/30"
    }`}
  >
    {label}
  </button>
);

interface TagChipProps {
  label: string;
}

const TagChip = ({ label }: TagChipProps) => (
  <span className="text-xs sm:text-sm text-white/80 border border-white/20 rounded-full px-3 py-1 leading-snug">
    {label}
  </span>
);

interface PaginationItemProps {
  index: number;
  label: string;
  active: boolean;
  onClick: () => void;
}

const PaginationItem = ({ index, label, active, onClick }: PaginationItemProps) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 rounded-full border transition-all duration-300 text-sm ${
      active
        ? "border-white/30 bg-white/10 text-white pl-3 pr-4 py-1.5"
        : "border-white/20 text-white/40 w-8 h-8 justify-center hover:text-white/70"
    }`}
  >
    <span className={`font-semibold ${active ? "text-yellow-100" : ""}`}>
      {index + 1}
    </span>
    {active && (
      <span className="max-w-[150px] truncate text-xs">{label}</span>
    )}
  </button>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const WhatWeDo = () => {
  const [active, setActive] = useState(0);
  const [visible, setVisible] = useState(true);
  const { openModal } = useModal();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activeRef = useRef(0);
  const current = SERVICES[active];

  const switchTo = useCallback((index: number) => {
    setVisible(false);
    setTimeout(() => {
      activeRef.current = index;
      setActive(index);
      setVisible(true);
    }, 220);
  }, []);

  const startAutoSwitch = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      const next = (activeRef.current + 1) % SERVICES.length;
      switchTo(next);
    }, AUTO_SWITCH_MS);
  }, [switchTo]);

  useEffect(() => {
    startAutoSwitch();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [startAutoSwitch]);

  const handleTabClick = (index: number) => {
    switchTo(index);
    startAutoSwitch();
  };

  return (
    <div className="w-full bg-blue-100 px-6 sm:px-10 xl:px-20 py-14 xl:py-20 flex flex-col gap-10">
      {/* Heading */}
      <h2 data-aos="fade-up" className="text-[28px] sm:text-[36px] xl:text-[40px] font-black text-white">
        What We do
      </h2>

      {/* Body */}
      <div data-aos="fade-up" data-aos-delay="100" className="flex flex-col xl:flex-row gap-8 xl:gap-12">
        {/* Left – Tab sidebar */}
        <nav className="flex xl:flex-col gap-1 overflow-x-auto xl:overflow-visible xl:w-[210px] shrink-0 pb-1 xl:pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {SERVICES.map((svc, i) => (
            <div key={svc.label} className="shrink-0 xl:shrink xl:w-full">
              <TabItem
                label={svc.label}
                active={active === i}
                onClick={() => handleTabClick(i)}
              />
            </div>
          ))}
        </nav>

        {/* Center – Image */}
        <div
          className={`xl:w-[340px] shrink-0 transition-all duration-300 ease-out ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          <div className="w-full aspect-[4/3] sm:aspect-[4/5] rounded-2xl overflow-hidden">
            <img
              src={current.image}
              alt={current.label}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Right – Content */}
        <div
          className={`flex-1 flex flex-col gap-5 justify-center transition-all duration-300 ease-out ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          <h3 className="text-xl sm:text-[22px] xl:text-[26px] font-bold text-white leading-snug">
            {current.title}
          </h3>

          <div className="flex flex-col gap-3">
            {current.description.map((para, i) => (
              <p key={i} className="text-white/70 text-sm sm:text-base leading-relaxed">
                {para}
              </p>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            {current.tags.map((tag) => (
              <TagChip key={tag} label={tag} />
            ))}
          </div>

          {current.badge && (
            <div className="flex items-center gap-2">
              <img src={clock} alt="clock" className="w-5 h-5 opacity-70" />
              <span className="text-sm text-white/60">{current.badge}</span>
            </div>
          )}

          <button
            onClick={() => openModal(current.modal)}
            className="w-fit px-6 py-3 bg-yellow-100 text-blue-100 font-semibold rounded-full hover:bg-yellow-200 transition-colors text-sm"
          >
            {current.cta}
          </button>
        </div>
      </div>

      {/* Bottom pagination */}
      <div className="flex items-center gap-3 justify-center pt-2">
        {SERVICES.map((svc, i) => (
          <PaginationItem
            key={svc.label}
            index={i}
            label={svc.label}
            active={active === i}
            onClick={() => handleTabClick(i)}
          />
        ))}
      </div>
    </div>
  );
};

export default WhatWeDo;
